/**
 * AppContext - React context for global app actions and navigation.
 *
 * Provides methods for navigation, creating documents/courses, system type detection, and search modal state.
 * Used to coordinate global UI actions and dialogs across the app.
 */
import { createContext, useContext, useEffect, useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Search } from "../components/Search";
import { useShortcuts } from "../hooks/use-shortcuts";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import { CreateCourseModal } from "@/components/course_components/CreateCourseDialog";
import type { Id } from "../../convex/_generated/dataModel";
import { useGeneralFeatureFlags } from "@/contexts/general-feature-flags-context";
import { notifyGeneralFeatureUnavailable } from "@/lib/general-features-flag";
import { useTheme } from "@/contexts/theme-context";
import { FeedbackDialog } from "@/components/ui/FeedbackDialog";
import { useUser, useAuth } from "@clerk/react";

const CreateDocumentDialog = lazy(() =>
  import("@/components/dashboard_components/CreateDocumentDialog").then((m) => ({
    default: m.CreateDocumentDialog,
  }))
);

interface CourseData {
  courseName?: string;
  courseCode?: string;
  professorName?: string;
  yearSemester?: string;
  additionalInfo?: string;
}

interface AppContextType {
  navigate: (path: string) => void;
  handleCreateDoc: (
    e?: React.MouseEvent,
    courseId?: string,
    subfolderId?: string,
    options?: {
      initialStep?: "initial" | "normal-form" | "pdf-form";
      initialPdfFile?: File | null;
    }
  ) => void;
  handleCreateCourse: (courseData: CourseData) => void;
  handleCreateCourseClick: (e?: React.MouseEvent) => void;
  systemType: string;
  setIsSearchOpen: (isOpen: boolean) => void;
  setIsFeedbackOpen: (isOpen: boolean) => void;
  hasFreePlan: boolean;
  hasPlusPlan: boolean;
  hasScholarPlan: boolean;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { isEnabled, ensureEnabled } = useGeneralFeatureFlags();
  const { toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { has } = useAuth();
  const hasPlusPlan = has?.({ plan: "plus_plan" }) ?? false;
  const hasScholarPlan = has?.({ plan: "scholar_plan" }) ?? false;
  const hasFreePlan = !hasPlusPlan && !hasScholarPlan;
  const [systemType, setSystemType] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    if (isLoaded && user) {
      const showFeedback = user.unsafeMetadata?.feedbackdialog === "true" || user.unsafeMetadata?.feedbackdialog === true;
      if (showFeedback) {
        setIsFeedbackOpen(true);
      }
    }
  }, [isLoaded, user?.unsafeMetadata?.feedbackdialog]);
  const [isCreateDocOpen, setIsCreateDocOpen] = useState(false);
  const [defaultCourseId, setDefaultCourseId] = useState<string | undefined>();
  const [defaultSubfolderId, setDefaultSubfolderId] = useState<string | undefined>();
  const [initialStep, setInitialStep] = useState<"initial" | "normal-form" | "pdf-form">("initial");
  const [initialPdfFile, setInitialPdfFile] = useState<File | null>(null);
  const createCourse = useMutation(api.courses.create);
  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes("win")) {
      setSystemType("Windows");
    } else if (platform.includes("mac")) {
      setSystemType("macOS");
    } else {
      setSystemType("Other");
    }
  }, []);

  const handleCreateDoc = (
    e?: React.MouseEvent,
    courseId?: string,
    subfolderId?: string,
    options?: {
      initialStep?: "initial" | "normal-form" | "pdf-form";
      initialPdfFile?: File | null;
    }
  ) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!isEnabled("blankNote") && !isEnabled("pdfNote")) {
      notifyGeneralFeatureUnavailable();
      return;
    }
    setDefaultCourseId(courseId);
    setDefaultSubfolderId(subfolderId);
    setInitialStep(options?.initialStep || "initial");
    setInitialPdfFile(options?.initialPdfFile || null);
    setIsCreateDocOpen(true);
  };

  const handleCreateCourseClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (!ensureEnabled("course")) return;
    setIsModalOpen(true);
  };

  useShortcuts({
    onCommandK: () => setIsSearchOpen(true),
    onCommandShift1: handleCreateCourseClick,
    onCommandShift2: handleCreateDoc,
    onCommandShiftL: toggleTheme,
  });

  const handleCreateCourse = (courseData: CourseData) => {
    setIsCreating(true);
    const promise = createCourse({
      title: courseData?.courseName || "Untitled Course",
      courseCode: courseData?.courseCode || undefined,
      professorName: courseData?.professorName || undefined,
      yearSemester: courseData?.yearSemester || undefined,
      additionalInfo: courseData?.additionalInfo || undefined
    })
      .then(() => {
        toast.success("Course created successfully!");
      })
      .catch((error) => {
        console.error("Failed to create course:", error);
        toast.error("Failed to create course");
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const contextValue: AppContextType = {
    navigate,
    handleCreateDoc,
    handleCreateCourse,
    handleCreateCourseClick,
    systemType,
    setIsSearchOpen,
    setIsFeedbackOpen,
    hasFreePlan,
    hasPlusPlan,
    hasScholarPlan,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
      <Search />
      {isCreateDocOpen && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/40">
              <Loader2 className="size-8 animate-spin text-muted-foreground" aria-label="Loading dialog" />
            </div>
          }
        >
          <CreateDocumentDialog
            open={isCreateDocOpen}
            onOpenChange={(open) => {
              setIsCreateDocOpen(open);
              if (!open) {
                setInitialStep("initial");
                setInitialPdfFile(null);
              }
            }}
            defaultCourseId={defaultCourseId as Id<"courses"> | undefined}
            defaultSubfolderId={defaultSubfolderId as Id<"courseSubfolders"> | undefined}
            initialStep={initialStep}
            initialPdfFile={initialPdfFile}
          />
        </Suspense>
      )}
      <CreateCourseModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <FeedbackDialog 
        open={isFeedbackOpen}
        onOpenChange={setIsFeedbackOpen}
      />
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
}; 